import React, { useState } from "react"
import { Modal, Tag, Alert } from "antd"

import Scheduling from "../../Apis/schedulingApi"
import {
  BOOKING_STATUS_TAGS,
  canCancel,
  displayStatus,
  errorTextFrom,
  formatDate,
  formatTimeRange,
  isUnresolved,
} from "./schedulingUtils"
import "./Scheduling.css"

/**
 * Operational detail for one meeting. Deliberately excludes anything from the
 * provider that is not safe to show: no tokens, no raw responses, no traces.
 */
const MeetingDetailModal = ({ booking, open, onClose, onChanged, onCancelRequest, t, lang, timezone }) => {
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState("")

  if (!booking) return null

  const zone = timezone || booking.timezone
  const status = displayStatus(booking)
  const tag = BOOKING_STATUS_TAGS[status] || BOOKING_STATUS_TAGS.pending

  const checkStatus = async () => {
    try {
      setChecking(true)
      setError("")
      const response = await Scheduling.getBooking(booking.id, { refresh: true })
      onChanged?.(response?.data?.booking)
    } catch (caught) {
      setError(errorTextFrom(t, caught, t?.error_generic))
    } finally {
      setChecking(false)
    }
  }

  const rows = [
    [t?.detail_attendee || "Attendee", booking.attendee_name],
    [t?.detail_email || "Email", <span dir="ltr">{booking.attendee_email}</span>],
    [
      t?.detail_guests || "Additional guests",
      booking.guest_emails?.length ? (
        <span dir="ltr">{booking.guest_emails.join(", ")}</span>
      ) : (
        <span className="sch-muted">{t?.detail_no_guests || "None"}</span>
      ),
    ],
    [t?.detail_message || "Message", booking.message || "—"],
    [t?.detail_attendee_timezone || "Attendee time zone", booking.timezone],
    [
      t?.detail_organizer_time || "Organizer time",
      <span>
        {formatDate(booking.start_at, zone, lang)}{" "}
        <span dir="ltr">{formatTimeRange(booking.start_at, booking.end_at, zone, lang)}</span>
      </span>,
    ],
    [
      t?.detail_duration || "Duration",
      `${booking.duration_minutes} ${t?.minutes || "minutes"}`,
    ],
    [
      t?.detail_source || "Source",
      booking.source === "admin" ? t?.source_admin || "Admin" : t?.source_website || "Website",
    ],
    [t?.detail_booked_at || "Booked at", formatDate(booking.booked_at, zone, lang)],
    [t?.detail_status || "Status", <Tag color={tag.color}>{t?.[tag.key] || status}</Tag>],
    [
      t?.detail_meet || "Joining link",
      booking.meet_url ? (
        <a className="sch-link" dir="ltr" href={booking.meet_url} target="_blank" rel="noreferrer">
          {booking.meet_url}
        </a>
      ) : (
        <span className="sch-muted">
          {booking.conference_status === "failed"
            ? t?.meet_failed || "No link — share joining details with the attendee"
            : t?.meet_pending || "Being prepared"}
        </span>
      ),
    ],
  ]

  if (booking.event_url) {
    rows.push([
      t?.detail_calendar || "Calendar",
      <a className="sch-link" dir="ltr" href={booking.event_url} target="_blank" rel="noreferrer">
        {t?.open_in_calendar || "Open in Google Calendar"}
      </a>,
    ])
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={620}
      centered
      title={t?.detail_title || "Meeting details"}
      className="scheduling-modal"
    >
      {error ? <Alert type="error" showIcon message={error} className="sch-alert" /> : null}

      <dl className="sch-detail-list">
        {rows.map(([label, value]) => (
          <div className="sch-detail-row" key={label}>
            <dt className="sch-detail-label">{label}</dt>
            <dd className="sch-detail-value">{value}</dd>
          </div>
        ))}
      </dl>

      <div className="sch-modal-actions">
        {isUnresolved(booking) ? (
          <button type="button" className="sch-btn sch-btn-secondary" disabled={checking} onClick={checkStatus}>
            {checking ? t?.checking || "Checking…" : t?.check_status || "Check status"}
          </button>
        ) : null}
        {canCancel(booking) ? (
          <button type="button" className="sch-btn sch-btn-danger" onClick={() => onCancelRequest?.(booking)}>
            {t?.cancel_meeting || "Cancel meeting"}
          </button>
        ) : null}
      </div>
    </Modal>
  )
}

export default MeetingDetailModal
